import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import katex from 'katex';
import { db, auth } from "@/firebase/config";
import { collection, addDoc, serverTimestamp, query, where, onSnapshot } from "firebase/firestore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, Printer, Clock, Layers, BookOpen, CheckCircle, CheckCircle2, Smartphone, ShieldCheck, Link as LinkIcon, Copy, QrCode, AlertCircle, Users, Eye, ArrowLeft, FileText, ChevronDown } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { DiagramBlock } from './components/DiagramBlock';
import { renderToStaticMarkup } from 'react-dom/server';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

import { Textarea } from "@/components/ui/textarea"; 
import { useToast } from "@/hooks/use-toast";
    
interface Question {
  id: string;
  type: string;
  text: string;
  marks: number;
  options?: string[];
  correctAnswerIndex?: number;
  modelAnswer?: string;
  subQuestions?: any[];
  section?: string;
}

interface AssessmentPreviewProps {
  data: {
    title: string;
    type: string;
    description?: string;
    duration?: string;

    totalMarks: number;
    questions: Question[];
    id?: string;
  };
  onBack: () => void;
  onEdit: () => void;
}

const splitTextByDiagrams = (text: string) => {
    const regex = /(%%DIAGRAM%%.*?%%)/s;
    return text.split(regex).filter(Boolean);
};

const MarkdownText = ({ children, className }: { children?: string; className?: string }) => {
    const textToProcess = typeof children === 'string' ? children : String(children || '');
    const processedText = textToProcess
        .replace(/\\\(([\s\S]*?)\\\)/g, (match) => '$' + match.slice(2, -2) + '$')
        .replace(/\\\[([\s\S]*?)\\\]/g, (match) => '$$' + match.slice(2, -2) + '$$');

    const parts = splitTextByDiagrams(processedText);

    return (
        <div className={`markdown-body ${className || ''}`}>
            {parts.map((part, index) => {
                if (part.startsWith('%%DIAGRAM%%') && part.endsWith('%%')) {
                    try {
                        const jsonStr = part.slice(11, -2).trim();
                        const spec = JSON.parse(jsonStr);
                        return (
                            <div key={index} className="flex justify-center my-4">
                                <div className="max-w-[400px] w-full">
                                    <DiagramBlock spec={spec} />
                                </div>
                            </div>
                        );
                    } catch {
                        return <div key={index} className="text-red-500 text-xs my-2">[Diagram Parse Error]</div>;
                    }
                }
                return (
                    <ReactMarkdown key={index} remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {part}
                    </ReactMarkdown>
                );
            })}
        </div>
    );
};

const renderMathInHtml = (html: string): string => {
    if (!html) return '';
    let result = html.replace(/\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]/g, (_m, g1, g2) => {
        try { return katex.renderToString((g1 ?? g2).trim(), { displayMode: false, throwOnError: false }); }
        catch { return _m; }
    });
    result = result.replace(/(?<!\$)\$(?!\$)([^$\n]+?)\$(?!\$)|\\\(([\s\S]+?)\\\)/g, (_m, g1, g2) => {
        try { return katex.renderToString((g1 ?? g2).trim(), { displayMode: false, throwOnError: false }); }
        catch { return _m; }
    });
    return result;
};

const AssessmentPreview: React.FC<AssessmentPreviewProps> = ({ data, onBack, onEdit }) => {
  const { toast } = useToast();
  const [mode, setMode] = useState<'student' | 'moderator' | 'issue_assessment' | 'results'>('student');
  const [studentAnswers, setStudentAnswers] = useState<Record<string, number>>({});
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<any | null>(null);
  
  // Fetch Submissions
  useEffect(() => {
    if (mode === 'results' && data.id && auth.currentUser) {
      setLoadingResults(true);
      const q = query(
        collection(db, "submissions"), 
        where("originalAssessmentId", "==", data.id),
        where("student.teacherId", "==", auth.currentUser.uid)
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const fetched = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSubmissions(fetched);
        setLoadingResults(false);
      }, (error) => {
        console.error("Error fetching submissions:", error);
        setLoadingResults(false);
      });
      return () => unsubscribe();
    }
  }, [mode, data.id, auth.currentUser]);

  const calculateMCQScore = (submissionAnswers: any) => {
      let score = 0;
      if (!submissionAnswers) return 0;
      data.questions.forEach(q => {
          if (q.type === 'Multiple Choice' && q.options && q.correctAnswerIndex !== undefined) {
              const studentAnswer = submissionAnswers[q.id];
              const correctAnswer = q.options[q.correctAnswerIndex];
              if (studentAnswer === correctAnswer) {
                  score += q.marks;
              }
          }
          if (q.subQuestions) {
              q.subQuestions.forEach((sq: any) => {
                  if (sq.type === 'Multiple Choice' && sq.options && sq.correctAnswerIndex !== undefined) {
                      const studentAnswer = submissionAnswers[sq.id];
                      const correctAnswer = sq.options[sq.correctAnswerIndex];
                      if (studentAnswer === correctAnswer) {
                          score += sq.marks;
                      }
                  }
              });
          }
      });
      return score;
  };

  const totalMCQPossible = () => {
      let total = 0;
      data.questions.forEach(q => {
          if (q.type === 'Multiple Choice') total += q.marks;
          if (q.subQuestions) {
              q.subQuestions.forEach((sq: any) => {
                  if (sq.type === 'Multiple Choice') total += sq.marks;
              });
          }
      });
      return total;
  };

  const formatTimestamp = (ts: any) => {
      if (!ts) return 'N/A';
      try {
          // Firebase Timestamp to Date
          const date = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
          return date.toLocaleString([], { 
              month: 'short', 
              day: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit' 
          });
      } catch (e) {
          return 'N/A';
      }
  };

  const calculateDuration = (startedAt: any, submittedAt: any) => {
      if (!startedAt || !submittedAt) return 'N/A';
      try {
          const end = submittedAt.toDate ? submittedAt.toDate().getTime() : (submittedAt.seconds * 1000);
          const start = typeof startedAt === 'number' ? startedAt : (startedAt.toDate ? startedAt.toDate().getTime() : 0);
          
          if (!start) return 'N/A';
          
          const diffMs = end - start;
          const mins = Math.floor(diffMs / 60000);
          const secs = Math.floor((diffMs % 60000) / 1000);
          
          return `${mins}m ${secs}s`;
      } catch (e) {
          return 'N/A';
      }
  };

  const renderSubmissionDetail = (sub: any) => {
      return (
          <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
              <div className="flex items-center justify-between border-b border-gray-100 dark:border-white/5 pb-4">
                  <div className="flex items-center gap-3">
                      <Button variant="ghost" size="sm" onClick={() => setSelectedSubmission(null)} className="h-8 w-8 p-0 rounded-full">
                          <ArrowLeft className="w-4 h-4" />
                      </Button>
                      <div>
                          <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100">{sub.student?.name}</h3>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span className="font-mono">{sub.student?.regNo}</span>
                              <span>•</span>
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatTimestamp(sub.submittedAt)}</span>
                              <span>•</span>
                              <span className="font-bold text-blue-600">Time Taken: {calculateDuration(sub.startedAt, sub.submittedAt)}</span>
                          </div>
                      </div>
                  </div>
                  <Badge className="bg-blue-600">
                      Total MCQ: {calculateMCQScore(sub.answers)} / {totalMCQPossible()}
                  </Badge>
              </div>

              <div className="space-y-4">
                  {data.questions.map((q, idx) => {
                      const isSectionStart = idx === 0 || data.questions[idx - 1].section !== q.section;
                      const studentAnswer = sub.answers?.[q.id];
                      const isMCQ = q.type === 'Multiple Choice';
                      const isCorrect = isMCQ && studentAnswer === q.options?.[q.correctAnswerIndex!];

                      return (
                          <div key={q.id} className="space-y-3">
                              {isSectionStart && q.section && (
                                  <div className="flex items-center gap-3 pt-2">
                                      <div className="h-px flex-1 bg-gray-200 dark:bg-white/10" />
                                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{q.section}</span>
                                      <div className="h-px flex-1 bg-gray-200 dark:bg-white/10" />
                                  </div>
                              )}

                              <div className="p-4 rounded-xl border border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/5 space-y-3">
                                  <div className="flex items-start gap-3">
                                      <Badge variant="outline" className="shrink-0 h-6 w-6 rounded-md flex items-center justify-center p-0 font-bold border-gray-300">
                                          {idx + 1}
                                      </Badge>
                                      <div className="space-y-1 flex-1">
                                          <MarkdownText className="text-sm font-medium leading-relaxed text-gray-900 dark:text-gray-100">
                                              {q.text}
                                          </MarkdownText>
                                          <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
                                              {q.marks} Marks • {q.type}
                                          </span>

                                          {!q.subQuestions?.length && (
                                              <>
                                                  <div className="mt-3 p-3 rounded-lg bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10">
                                                      <Label className="text-[10px] text-muted-foreground uppercase font-bold block mb-1">Student Answer:</Label>
                                                      <div className="flex items-center gap-2">
                                                          {isMCQ && (
                                                              <div className={`p-1 rounded-full ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                                                                  {isCorrect ? <CheckCircle className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5 truncate" />}
                                                              </div>
                                                          )}
                                                          <div className={`text-sm font-semibold ${isMCQ ? (isCorrect ? 'text-green-600' : 'text-red-600') : 'text-blue-600'}`}>
                                                              {studentAnswer ? (
                                                                  <MarkdownText>{String(studentAnswer)}</MarkdownText>
                                                              ) : (
                                                                  <span className="italic text-muted-foreground font-normal">No Response</span>
                                                              )}
                                                          </div>
                                                      </div>
                                                  </div>

                                                  {!isCorrect && (
                                                      <div className="mt-2 p-3 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20">
                                                          <Label className="text-[10px] text-blue-600 uppercase font-bold block mb-1">Expected / Model Answer:</Label>
                                                          {isMCQ ? (
                                                              <MarkdownText className="text-xs text-blue-700 dark:text-blue-400 font-medium">
                                                                  {q.options?.[q.correctAnswerIndex!] || ''}
                                                              </MarkdownText>
                                                          ) : (
                                                              <div
                                                                  className="text-xs text-blue-700 dark:text-blue-400 font-medium overflow-x-auto"
                                                                  dangerouslySetInnerHTML={{ __html: renderMathInHtml(formatMarkSchemeAnswer(q.modelAnswer || 'Refer to marking scheme')) }}
                                                              />
                                                          )}
                                                      </div>
                                                  )}
                                              </>
                                          )}

                                          {q.subQuestions && q.subQuestions.length > 0 && (
                                              <div className="mt-4 space-y-3 border-l border-gray-200 dark:border-white/10 pl-4">
                                                  {q.subQuestions.map((sq: any, sqIdx: number) => {
                                                      const subStudentAnswer = sub.answers?.[sq.id];
                                                      const subIsMCQ = sq.type === 'Multiple Choice';
                                                      const subIsCorrect = subIsMCQ && subStudentAnswer === sq.options?.[sq.correctAnswerIndex!];

                                                      return (
                                                          <div key={sq.id || sqIdx} className="rounded-lg border border-gray-100 dark:border-white/10 bg-white dark:bg-white/5 p-3 space-y-2">
                                                              <div className="flex items-start gap-3">
                                                                  <span className="text-xs font-bold text-gray-500 dark:text-gray-400 shrink-0">({String.fromCharCode(97 + sqIdx)})</span>
                                                                  <div className="flex-1 space-y-1">
                                                                      <MarkdownText className="text-sm text-gray-900 dark:text-gray-100">
                                                                          {sq.text}
                                                                      </MarkdownText>
                                                                      <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
                                                                          {sq.marks} Marks • {sq.type}
                                                                      </span>
                                                                  </div>
                                                              </div>

                                                              <div className="p-3 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10">
                                                                  <Label className="text-[10px] text-muted-foreground uppercase font-bold block mb-1">Student Answer:</Label>
                                                                  <div className="flex items-center gap-2">
                                                                      {subIsMCQ && (
                                                                          <div className={`p-1 rounded-full ${subIsCorrect ? 'text-green-600' : 'text-red-600'}`}>
                                                                              {subIsCorrect ? <CheckCircle className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5 truncate" />}
                                                                          </div>
                                                                      )}
                                                                      <div className={`text-sm font-semibold ${subIsMCQ ? (subIsCorrect ? 'text-green-600' : 'text-red-600') : 'text-blue-600'}`}>
                                                                          {subStudentAnswer ? (
                                                                              <MarkdownText>{String(subStudentAnswer)}</MarkdownText>
                                                                          ) : (
                                                                              <span className="italic text-muted-foreground font-normal">No Response</span>
                                                                          )}
                                                                      </div>
                                                                  </div>
                                                              </div>

                                                              {!subIsCorrect && (
                                                                  <div className="p-3 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20">
                                                                      <Label className="text-[10px] text-blue-600 uppercase font-bold block mb-1">Expected / Model Answer:</Label>
                                                                      {subIsMCQ ? (
                                                                          <MarkdownText className="text-xs text-blue-700 dark:text-blue-400 font-medium">
                                                                              {sq.options?.[sq.correctAnswerIndex!] || ''}
                                                                          </MarkdownText>
                                                                      ) : (
                                                                          <div
                                                                              className="text-xs text-blue-700 dark:text-blue-400 font-medium overflow-x-auto"
                                                                              dangerouslySetInnerHTML={{ __html: renderMathInHtml(formatMarkSchemeAnswer(sq.modelAnswer || 'Refer to marking scheme')) }}
                                                                          />
                                                                      )}
                                                                  </div>
                                                              )}
                                                          </div>
                                                      );
                                                  })}
                                              </div>
                                          )}
                                      </div>
                                  </div>
                              </div>
                          </div>
                      );
                  })}
              </div>
          </div>
      );
  };
  
  // Issue State
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [issueSettings, setIssueSettings] = useState({
      duration: parseInt(data.duration || "40"),
      expiresIn: "24", // hours
      requireRegNo: true,
      secureBrowser: true
  });

  // Format model answer for Mark Scheme: Answer in orange, Explanation in green
  const formatMarkSchemeAnswer = (text: string | null | undefined): string => {
      if (!text) return '<span class="text-gray-400 italic">No model answer provided.</span>';
      
      let content = String(text).trim();
      
      if (!content) {
          return '<span class="text-gray-400 italic">No model answer provided.</span>';
      }
      
      // Split content into answer and explanation parts
      const stepMatch = content.match(/Step\s+\d+/i);
      let answerPart = '';
      let explanationPart = '';
      
      if (stepMatch && stepMatch.index !== undefined && stepMatch.index > 0) {
          answerPart = content.substring(0, stepMatch.index).trim();
          explanationPart = content.substring(stepMatch.index).trim();
      } else if (stepMatch && stepMatch.index === 0) {
          // Content starts with Step - try to find a final result
          const finalMatch = content.match(/(?:Final|Result|Answer|Therefore|Hence)[:\s]+([^\n]+)/i) 
                          || content.match(/(?:dy\/dx|f'?\(x\)|y')\s*=\s*([^\n\[\(]+)/i);
          if (finalMatch && finalMatch[1]) {
              answerPart = finalMatch[1].replace(/\[.*marks.*\]/i, '').trim();
          } else {
              answerPart = 'See steps below';
          }
          explanationPart = content;
      } else {
          const lines = content.split(/\n+/).filter(l => l.trim());
          answerPart = lines[0] || '';
          explanationPart = lines.slice(1).join('\n');
      }
      
      let html = '';
      
      // Answer in orange box
      if (answerPart) {
          html += `<div class="text-orange-600 dark:text-orange-400 font-semibold p-2 bg-orange-50 dark:bg-orange-900/20 rounded border-l-4 border-orange-400 mb-2 overflow-x-auto break-words">${answerPart}</div>`;
      }
      
      // Explanation in green box
      if (explanationPart) {
          const stepParts = explanationPart.split(/(Step\s+\d+:?)/i).filter(s => s.trim());
          let formattedSteps: string[] = [];
          
          for (let i = 0; i < stepParts.length; i++) {
              const part = stepParts[i].trim();
              if (/^Step\s+\d+:?$/i.test(part) && stepParts[i + 1]) {
                  formattedSteps.push(`<span class="font-bold text-green-700 dark:text-green-300">${part}</span> ${stepParts[i + 1].trim()}`);
                  i++;
              } else if (!/^Step\s+\d+:?$/i.test(part)) {
                  formattedSteps.push(part);
              }
          }
          
          const explanationHtml = formattedSteps.join('<br/><br/>');
          html += `<div class="text-green-800 dark:text-green-300 text-xs p-2 bg-green-50 dark:bg-green-900/20 rounded border-l-4 border-green-400 overflow-x-auto break-words">${explanationHtml}</div>`;
      }
      
      if (!html) {
          html = `<div class="text-orange-600 dark:text-orange-400 font-medium">${content}</div>`;
      }
      
      return html;
  };

  const splitTextByDiagramsHtml = (text: string) => {
    const regex = /(%%DIAGRAM%%.*?%%)/s;
    return text.split(regex).filter(Boolean);
  };

  const processTextWithDiagrams = (text: string, mathFn?: (t: string) => string): string => {
    if (!text) return '';
    const parts = splitTextByDiagramsHtml(text);
    return parts.map(part => {
      if (part.startsWith('%%DIAGRAM%%') && part.endsWith('%%')) {
        try {
          const jsonStr = part.slice(11, -2).trim();
          const spec = JSON.parse(jsonStr);
          return `<div style="display:flex;justify-content:center;margin:1rem 0"><div style="max-width:400px;width:100%">${renderToStaticMarkup(<DiagramBlock spec={spec} />)}</div></div>`;
        } catch {
          return '<span style="color:red;font-size:12px">[Diagram Parse Error]</span>';
        }
      }
      return mathFn ? mathFn(part) : part;
    }).join('');
  };

  const buildExamHtml = (includeAnswers: boolean): string => {
    const katexCss = '<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css">';
    const styles = `
      <style>
        body { font-family: 'Times New Roman', Times, serif; font-size: 11pt; line-height: 1.4; color: #333; margin: 0; padding: 30px; }
        h1 { font-size: 16pt; text-align: center; text-transform: uppercase; margin-bottom: 4px; }
        .meta { text-align: center; font-size: 10pt; color: #555; margin-bottom: 20px; border-bottom: 2px double #333; padding-bottom: 10px; }
        h2 { font-size: 13pt; border-bottom: 1px solid #333; padding-bottom: 4px; margin-top: 24px; }
        .instructions { font-style: italic; font-size: 10pt; color: #555; margin-bottom: 10px; }
        .q { margin-bottom: 14px; page-break-inside: avoid; }
        .q-text { margin-bottom: 4px; line-height: 1.6; }
        ol.opts { margin: 6px 0 6px 24px; padding: 0; }
        ol.opts li { margin-bottom: 5px; line-height: 1.6; }
        .sub-list { margin: 6px 0 6px 28px; padding: 0; list-style: lower-alpha; }
        .sub-list li { margin-bottom: 8px; line-height: 1.6; }
        .answer-block { margin-top: 6px; border-left: 3px solid #cce; padding-left: 10px; background:#f8f9fa; break-inside: avoid; padding-bottom: 5px; }
        .answer-block h4 { font-size: 9pt; font-weight: bold; color: #0056b3; margin: 4px 0 2px; }
        .answer-text { color: #006400; font-size: 10pt; line-height: 1.6; }
        .answer-highlight { color: #d97706; font-weight: 600; }
        @media print { body { padding: 15px; } }
      </style>
    `;
    let body = `<h1>${data.title}</h1><div class="meta">${data.title} &mdash; ${data.type || 'Assessment'}</div>`;

    if (data.description) {
      body += `<p class="instructions">${processTextWithDiagrams(data.description, renderMathInHtml)}</p>`;
    }

    let lastSection = '';
    data.questions.forEach((q, idx) => {
      if (q.section && q.section !== lastSection) {
        lastSection = q.section;
        body += `<h2>${q.section}</h2>`;
      }

      body += `<div class="q"><div class="q-text">${idx + 1}. ${processTextWithDiagrams(q.text, renderMathInHtml)} <em>(${q.marks} marks)</em></div>`;

      if (q.type === 'Multiple Choice' && q.options && q.options.length > 0) {
        body += `<ol class="opts" type="A">${q.options.map(o => `<li>${renderMathInHtml(o)}</li>`).join('')}</ol>`;
      }

      if (q.subQuestions && q.subQuestions.length > 0) {
        body += `<ol class="sub-list">${q.subQuestions.map((sq: any) =>
          `<li>${processTextWithDiagrams(sq.text, renderMathInHtml)} <em>(${sq.marks} marks)</em>${
            sq.type === 'Multiple Choice' && sq.options && sq.options.length > 0
              ? `<ol type="A" style="margin-top:4px;margin-left:20px">${sq.options.map((o: string) => `<li>${renderMathInHtml(o)}</li>`).join('')}</ol>`
              : ''
          }</li>`
        ).join('')}</ol>`;
      }

      if (includeAnswers) {
        const isNotNA = (text: string) => {
          if (!text) return false;
          const t = text.replace(/[*_]/g, '').trim().toUpperCase();
          return t !== 'N/A' && t !== 'NONE' && t !== 'NA';
        };
        const hasAnswer = isNotNA(q.modelAnswer || '');
        const subHasAnswer = (q.subQuestions || []).some((sq: any) => isNotNA(sq.modelAnswer || sq.correctAnswer));

        if (hasAnswer || subHasAnswer) {
          body += `<div class="answer-block"><h4>Answer / Marking Scheme:</h4>`;
          if (hasAnswer) {
            body += `<div class="answer-text">${renderMathInHtml(q.modelAnswer || '')}</div>`;
          }
          if (q.subQuestions && q.subQuestions.length > 0 && subHasAnswer) {
            body += `<ol class="sub-list" style="color:#006400">${q.subQuestions.map((sq: any) => {
              const ans = sq.modelAnswer || sq.correctAnswer || '';
              return `<li>${renderMathInHtml(isNotNA(ans) ? ans : 'See marking scheme.')}</li>`;
            }).join('')}</ol>`;
          }
          body += `</div>`;
        }
      }

      body += `</div>`;
    });

    return `<html><head><title>${data.title}</title>${katexCss}${styles}</head><body>${body}</body></html>`;
  };

  const handleExportPDF = (withAnswers: boolean) => {
    const html = buildExamHtml(withAnswers);
    const popup = window.open('', '', 'height=900,width=900');
    if (!popup) { alert('Please allow popups to export as PDF'); return; }
    popup.document.write(html);
    popup.document.close();
    setTimeout(() => popup.print(), 1000);
  };

  const handleGenerateLink = async () => {
    try {
        const user = auth.currentUser;
        if (!user) {
            console.error("No authenticated user");
            toast({
                title: "Authentication Error",
                description: "You must be logged in to issue an assessment.",
                variant: "destructive"
            });
            return;
        }

        const issuedData = {
            originalAssessmentId: data.id || 'unknown',
            teacherId: user.uid,
            teacherName: user.displayName || 'Teacher',
            subject: data.title || 'General',
            questions: data.questions, // Embed questions for self-contained assessment
            createdAt: serverTimestamp(),
            expiresAt: new Date(Date.now() + parseInt(issueSettings.expiresIn) * 60 * 60 * 1000),
            config: {
                durationMinutes: issueSettings.duration,
                requireRegNo: issueSettings.requireRegNo,
                secureBrowser: issueSettings.secureBrowser
            },
            active: true
        };

        const docRef = await addDoc(collection(db, "issued_assessments"), issuedData);
        
        // Generate link with the new Doc ID
        setGeneratedLink(`${window.location.origin}/assessment/take/${docRef.id}`);
        toast({
            title: "Success",
            description: "Secure assessment link generated successfully.",
        });
    } catch (error: any) {
        console.error("Error issuing assessment:", error);
        toast({
            title: "Generation Failed",
            description: error.message || "Failed to generate assessment link.",
            variant: "destructive"
        });
    }
  };

  const copyToClipboard = () => {
      if (generatedLink) {
          navigator.clipboard.writeText(generatedLink);
          toast({
              title: "Copied!",
              description: "Link copied to clipboard.",
          });
      }
  };

  const handleSimulate = () => {
      // Create simulation payload
      const simulationData = {
          ...data,
          teacherId: auth.currentUser?.uid || 'sim-teacher',
          teacherName: auth.currentUser?.displayName || 'Teacher (Sim)',
          config: {
              durationMinutes: issueSettings.duration,
              requireRegNo: issueSettings.requireRegNo,
              secureBrowser: issueSettings.secureBrowser
          }
      };
      
      sessionStorage.setItem('simulation_data', JSON.stringify(simulationData));
      window.open('/assessment/take/simulation', '_blank');
  };

  const toggleAnswer = (qId: string, optionIndex: number) => {
      if (mode !== 'student') return;
      setStudentAnswers(prev => ({
          ...prev,
          [qId]: optionIndex
      }));
  };

  return (
    <div className="max-w-4xl mx-auto pb-10 px-0 sm:px-1 md:px-4 animate-in fade-in duration-500 overflow-x-hidden">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-8 no-print w-full">
        {/* Left: Back */}
        <Button 
            variant="ghost" 
            onClick={onBack}
            className="text-muted-foreground hover:text-blue-600 pl-0 gap-2 text-xs font-bold transition-colors"
        >
            <ChevronLeft className="w-4 h-4" /> Back to Dashboard
        </Button>

        {/* Center: Mode Toggle */}
        <Tabs value={mode} onValueChange={(v) => setMode(v as any)}>
            <TabsList className="h-9 bg-slate-100 dark:bg-white/5 p-0.5 rounded-lg border border-slate-200 dark:border-white/10 gap-0">
                <TabsTrigger 
                    value="student" 
                    className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold h-8 rounded-md gap-1.5 px-3 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-blue-600/30 transition-all"
                >
                    <Smartphone className="w-3.5 h-3.5" /> Student
                </TabsTrigger>
                <TabsTrigger 
                    value="moderator" 
                    className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold h-8 rounded-md gap-1.5 px-3 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-blue-600/30 transition-all"
                >
                    <ShieldCheck className="w-3.5 h-3.5" /> Moderator
                </TabsTrigger>
                <TabsTrigger 
                    value="issue_assessment" 
                    className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold h-8 rounded-md gap-1.5 px-3 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-blue-600/30 transition-all"
                >
                    <Layers className="w-3.5 h-3.5" /> Issue
                </TabsTrigger>
                <TabsTrigger 
                    value="results" 
                    className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold h-8 rounded-md gap-1.5 px-3 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-blue-600/30 transition-all"
                >
                    <CheckCircle className="w-3.5 h-3.5" /> Results
                </TabsTrigger>
            </TabsList>
        </Tabs>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  title="Export exam as PDF in a new window"
                  className="p-1.5 rounded-md transition-colors flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:bg-white/10 h-9 px-4 border border-input"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Print</span>
                  <ChevronDown className="w-3 h-3 hidden sm:inline" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-white/10 shadow-xl rounded-xl p-1">
                <DropdownMenuItem
                  onClick={() => handleExportPDF(false)}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 cursor-pointer"
                >
                  <FileText className="w-4 h-4 text-blue-500" />
                  <div>
                    <div className="font-medium">Questions Only</div>
                    <div className="text-[10px] text-muted-foreground">No answers or marking scheme</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleExportPDF(true)}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 cursor-pointer"
                >
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <div>
                    <div className="font-medium">With Marking Scheme</div>
                    <div className="text-[10px] text-muted-foreground">Includes answers & explanations</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button 
                onClick={onEdit}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 font-bold text-xs h-9 rounded-lg px-4 shadow-sm"
            >
                Edit Exam
            </Button>
        </div>
      </div>

      {/* Assessment Paper / Content Area */}
       {mode === 'issue_assessment' ? (
          <Card className="shadow-lg border-blue-100 dark:border-blue-900/20 bg-white dark:bg-[#0f1221] animate-in slide-in-from-bottom-4 duration-500">
              <CardHeader className="border-b border-gray-100 dark:border-white/5 pb-8">
                  <div className="flex items-center gap-3 mb-2">
                       <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600">
                           <LinkIcon className="w-5 h-5" />
                       </div>
                       <div>
                           <CardTitle className="text-lg font-bold text-gray-900 dark:text-gray-100">Issue Secure Assessment</CardTitle>
                           <CardDescription className="text-xs">Configure access settings and generate a unique link for students.</CardDescription>
                       </div>
                  </div>
              </CardHeader>
              <CardContent className="pt-6 sm:pt-8 px-4 sm:px-6 md:px-8 lg:px-20 min-h-[300px] sm:min-h-[400px]">
                  {!generatedLink ? (
                      <div className="max-w-xl mx-auto space-y-8">
                          {/* Configuration Form */}
                          <div className="space-y-6">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div className="space-y-2">
                                      <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Exam Duration (Mins)</Label>
                                      <div className="relative">
                                          <Clock className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                                          <Input 
                                            type="number" 
                                            className="pl-9 font-bold font-mono"
                                            value={issueSettings.duration}
                                            onChange={(e) => setIssueSettings(s => ({...s, duration: parseInt(e.target.value) || 0}))} 
                                          />
                                      </div>
                                  </div>
                                  <div className="space-y-2">
                                      <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Link Expires In (Hours)</Label>
                                      <div className="relative">
                                          <Clock className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                                          <Input 
                                            type="number" 
                                            className="pl-9 font-bold font-mono"
                                            value={issueSettings.expiresIn}
                                            onChange={(e) => setIssueSettings(s => ({...s, expiresIn: e.target.value}))} 
                                          />
                                      </div>
                                  </div>
                              </div>

                              <div className="p-4 rounded-xl border border-blue-100 dark:border-blue-900/20 bg-blue-50/50 dark:bg-blue-900/10 space-y-4">
                                  <div className="flex items-center justify-between">
                                      <div className="space-y-0.5">
                                          <Label className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                              Require Student Registration Info
                                              <Badge variant="outline" className="text-[9px] bg-blue-100 text-blue-700 border-blue-200">REQUIRED</Badge>
                                          </Label>
                                          <p className="text-[10px] text-muted-foreground">Students must enter their Name and Reg/Admission Number.</p>
                                      </div>
                                      <Switch checked={true} disabled />
                                  </div>
                                  
                                  <div className="h-[1px] bg-blue-200/50 dark:bg-blue-800/20" />

                                  <div className="flex items-center justify-between">
                                      <div className="space-y-0.5">
                                          <Label className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                              reCAPTCHA Protection
                                              <ShieldCheck className="w-3 h-3 text-green-600" />
                                          </Label>
                                          <p className="text-[10px] text-muted-foreground">Bot protection enabled for link access.</p>
                                      </div>
                                      <Switch checked={true} disabled />
                                  </div>
                              </div>
                          </div>

                          <Button size="lg" className="w-full bg-[#1C398E] hover:bg-[#152b6b] text-white font-bold h-12 gap-2 shadow-lg shadow-blue-900/20" onClick={handleGenerateLink}>
                              <LinkIcon className="w-4 h-4" /> Generate Secure Link
                          </Button>
                          
                          <div className="relative flex py-2 items-center">
                              <div className="flex-grow border-t border-gray-200 dark:border-white/10"></div>
                              <span className="flex-shrink-0 mx-4 text-xs font-bold text-muted-foreground uppercase">OR</span>
                              <div className="flex-grow border-t border-gray-200 dark:border-white/10"></div>
                          </div>

                          <Button variant="outline" className="w-full h-11 bg-white dark:bg-transparent border-dashed gap-2 text-muted-foreground hover:text-blue-600 hover:border-blue-200" onClick={handleSimulate}>
                             <Smartphone className="w-4 h-4" /> Simulate Link View (Test)
                          </Button>
                      </div>
                  ) : (
                      <div className="max-w-xl mx-auto space-y-8 animate-in zoom-in-50 duration-300">
                          <div className="text-center space-y-2">
                              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto text-green-600 mb-4">
                                  <CheckCircle className="w-8 h-8" />
                              </div>
                              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Assessment Link Ready</h3>
                              <p className="text-sm text-muted-foreground">This link is secure and tied to the configured settings.</p>
                          </div>

                          <div className="p-1 bg-gray-100 dark:bg-white/5 rounded-xl border border-gray-200 dark:border-white/10 flex items-center">
                              <div className="flex-1 px-4 py-3 font-mono text-sm text-gray-600 dark:text-gray-300 truncate">
                                  {generatedLink}
                              </div>
                              <Button size="sm" variant="ghost" className="h-10 px-4 rounded-lg hover:bg-white dark:hover:bg-white/10 gap-2" onClick={copyToClipboard}>
                                  <Copy className="w-4 h-4" /> Copy
                              </Button>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                               <Button variant="outline" className="h-11 border-dashed gap-2" disabled>
                                   <QrCode className="w-4 h-4" /> Show QR Code
                               </Button>
                               <Button variant="outline" className="h-11 border-dashed gap-2" onClick={() => setGeneratedLink(null)}>
                                   Create New Link
                               </Button>
                          </div>

                          <div className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-100 dark:border-yellow-900/20 rounded-lg">
                              <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
                              <div className="space-y-1">
                                  <p className="text-xs font-bold text-yellow-800 dark:text-yellow-500">Important Note</p>
                                  <p className="text-[10px] text-yellow-700 dark:text-yellow-400 leading-relaxed">
                                      Share this link directly with your students. Once they start the assessment, the <strong>{issueSettings.duration} minute</strong> timer will begin. The link will expire in <strong>{issueSettings.expiresIn} hours</strong>.
                                  </p>
                              </div>
                          </div>
                      </div>
                  )}
              </CardContent>
          </Card>
      ) : mode === 'results' ? (
          <Card className="shadow-lg border-gray-100 dark:border-white/5 bg-white dark:bg-[#0f1221] animate-in slide-in-from-bottom-4 duration-500">
              <CardHeader className="border-b border-gray-100 dark:border-white/5 pb-6">
                  <CardTitle className="text-lg font-bold">Student Results Summary</CardTitle>
                  <CardDescription className="text-xs">Real-time performance metrics for multiple choice sections.</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                  {loadingResults ? (
                      <div className="flex items-center justify-center h-40">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                  ) : submissions.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-60 text-center space-y-3">
                          <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-full">
                              <Users className="w-10 h-10 text-gray-300" />
                          </div>
                          <div className="space-y-1">
                              <p className="text-sm font-bold text-gray-900 dark:text-gray-100">No submissions yet.</p>
                                  <p className="text-xs text-muted-foreground italic">Share the link in the 'Issue' tab to collect results.</p>
                          </div>
                      </div>
                  ) : selectedSubmission ? (
                      renderSubmissionDetail(selectedSubmission)
                  ) : (
                      <div className="overflow-x-auto -mx-4 sm:mx-0">
                          <table className="w-full text-left border-collapse min-w-[600px]">
                              <thead>
                                  <tr className="border-b border-gray-100 dark:border-white/5">
                                      <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground w-10">#</th>
                                      <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Reg Number</th>
                                      <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Student Name</th>
                                      <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Submitted</th>
                                      <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Took</th>
                                      <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">MCQ Marks</th>
                                      <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Status</th>
                                      <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground text-right">Action</th>
                                  </tr>
                              </thead>
                              <tbody>
                                  {submissions.map((sub, idx) => {
                                      const mcqScore = calculateMCQScore(sub.answers);
                                      const possible = totalMCQPossible();
                                      return (
                                          <tr key={sub.id} className="border-b border-gray-50 dark:border-white/5 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors">
                                              <td className="py-4 px-2 text-xs font-bold text-muted-foreground">{idx + 1}</td>
                                              <td className="py-4 px-2 text-xs font-mono text-muted-foreground">{sub.student?.regNo || 'N/A'}</td>
                                              <td className="py-4 px-2 font-bold text-sm text-gray-900 dark:text-gray-100">{sub.student?.name || 'Unknown'}</td>
                                              <td className="py-4 px-2 text-[10px] font-medium text-muted-foreground">{formatTimestamp(sub.submittedAt)}</td>
                                              <td className="py-4 px-2 text-[10px] font-bold text-gray-700 dark:text-gray-300">{calculateDuration(sub.startedAt, sub.submittedAt)}</td>
                                              <td className="py-4 px-2">
                                                  <div className="flex items-center gap-2">
                                                      <span className="text-sm font-bold text-blue-600">{mcqScore}</span>
                                                      <span className="text-[10px] font-medium text-gray-400">/ {possible}</span>
                                                  </div>
                                              </td>
                                              <td className="py-4 px-2">
                                                  <Badge variant="outline" className="text-[9px] font-bold uppercase tracking-tighter bg-green-50 text-green-700 border-green-200">Completed</Badge>
                                              </td>
                                              <td className="py-4 px-2 text-right">
                                                  <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    className="h-8 gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 font-bold px-3"
                                                    onClick={() => setSelectedSubmission(sub)}
                                                  >
                                                      <Eye className="w-3.5 h-3.5" /> View
                                                  </Button>
                                              </td>
                                          </tr>
                                      );
                                  })}
                              </tbody>
                          </table>
                      </div>
                  )}
              </CardContent>
          </Card>
      ) : (
      <Card className="shadow-sm border-gray-200 dark:border-white/10 bg-white dark:bg-[#0f1221] print:shadow-none print:border-none rounded-none md:rounded-xl overflow-hidden">
        <CardHeader className="border-b border-gray-100 dark:border-white/5 py-4 sm:py-6 px-4 sm:px-6 md:px-10 bg-gray-50/50 dark:bg-white/5">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="space-y-1">
                    <div className="flex items-center gap-2">
                         <Badge variant="outline" className="text-blue-600 bg-blue-50/50 border-blue-100 px-2 py-0 uppercase tracking-tighter text-[9px] font-bold">
                            {data.type}
                         </Badge>
                         <span className="text-[10px] text-muted-foreground uppercase font-mono tracking-widest">{data.duration || "40 Mins"}</span>
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        {data.title}
                    </CardTitle>
                    <CardDescription className="text-xs font-medium">
                        <MarkdownText>{data.description || "Instructions: Answer all questions in the spaces provided."}</MarkdownText>
                    </CardDescription>
                </div>
                
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[10px] font-bold text-muted-foreground uppercase tracking-wider bg-white dark:bg-white/5 p-3 rounded-lg border border-gray-100 dark:border-white/10">
                    <div className="flex items-center gap-2">
                        <Layers className="w-3 h-3 text-blue-500" /> Max Marks: {data.totalMarks}
                    </div>
                    <div className="flex items-center gap-2">
                        <BookOpen className="w-3 h-3 text-blue-500" /> Questions: {data.questions.length}
                    </div>
                    <div className="flex items-center gap-2 col-span-2 mt-1 pt-1 border-t border-gray-100 dark:border-white/5">
                        <Clock className="w-3 h-3 text-blue-500" /> Session: 2023/2024
                    </div>
                </div>
            </div>
        </CardHeader>

        <CardContent className="pt-3 sm:pt-6 px-3 sm:px-6 md:px-10 lg:px-12 space-y-4 sm:space-y-8 pb-6 sm:pb-12 min-h-[300px] sm:min-h-[600px] overflow-x-hidden">
            {data.questions.map((q, i) => {
                 const isSectionStart = i === 0 || data.questions[i-1].section !== q.section;
                 return (
                    <div key={q.id} className="group">
                         {isSectionStart && q.section && (
                             <div className="flex items-center gap-4 mb-5 mt-4">
                                <div className="h-[1px] bg-gray-900/10 dark:bg-white/10 flex-1" />
                                <span className="font-bold text-xs text-gray-900 dark:text-gray-100 uppercase tracking-[0.2em]">{q.section}</span>
                                <div className="h-[1px] bg-gray-900/10 dark:bg-white/10 flex-1" />
                             </div>
                         )}

                         <div className="flex gap-3 sm:gap-4 items-start relative">
                             <div className="font-bold text-sm text-gray-900 dark:text-gray-100 w-6 sm:w-8 pt-0.5 shrink-0">
                                {i + 1}.
                             </div>
                             <div className="flex-1 min-w-0 space-y-3 pb-4">
                                 <div>
                                                                         <MarkdownText className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                                                                             {q.text}
                                                                         </MarkdownText>
                                 </div>

                                 {/* Options for MCQ */}
                                 {q.type === 'Multiple Choice' && q.options && (
                                     <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 pl-2">
                                         {q.options.map((opt, idx) => (
                                             <div key={idx} 
                                                className="flex items-center gap-2 group/option cursor-pointer"
                                                onClick={() => toggleAnswer(q.id, idx)}
                                             >
                                                 <div className={`w-4 h-4 rounded-full border flex items-center justify-center text-[9px] font-bold shrink-0 transition-all ${
                                                     mode === 'student' 
                                                        ? (studentAnswers[q.id] === idx 
                                                            ? 'bg-blue-600 border-blue-600 text-white shadow-sm scale-110' 
                                                            : 'border-gray-300 dark:border-white/20 group-hover/option:border-blue-500 group-hover/option:text-blue-600')
                                                        : (q.correctAnswerIndex === idx ? 'bg-green-600 border-green-600 text-white' : 'border-gray-200 opacity-50')
                                                 }`}>
                                                    {mode === 'moderator' && q.correctAnswerIndex === idx ? <CheckCircle className="w-2.5 h-2.5" /> : String.fromCharCode(65 + idx)}
                                                 </div>
                                                                                                 <div className={`text-xs ${mode === 'moderator' && q.correctAnswerIndex === idx ? 'text-green-700 font-bold' : 'text-gray-700 dark:text-gray-300'}`}>
                                                                                                     <MarkdownText>{opt}</MarkdownText>
                                                                                                 </div>
                                             </div>
                                         ))}
                                     </div>
                                 )}

                                 {/* Space for written answers - ONLY if no subquestions */}
                                 {(q.type === 'Short Answer' || q.type === 'Essay') && (!q.subQuestions || q.subQuestions.length === 0) && (
                                     <div className="mt-2 text-sm">
                                         {mode === 'student' ? (
                                            q.type === 'Essay' ? (
                                                <Textarea placeholder="Type your full essay response here..." className="bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10 min-h-[200px] resize-y focus-visible:ring-blue-500/20" />
                                            ) : (
                                                <Textarea placeholder="Type your answer..." className="bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10 min-h-[100px] resize-y focus-visible:ring-blue-500/20" />
                                            )
                                         ) : (
                                             <div className="space-y-2">
                                                 <span className="text-[10px] font-bold text-blue-700 uppercase tracking-widest block">Model Answer</span>
                                                 <div dangerouslySetInnerHTML={{ __html: renderMathInHtml(formatMarkSchemeAnswer(q.modelAnswer)) }} className="overflow-x-auto" />
                                             </div>
                                         )}
                                     </div>
                                 )}

                                 {/* Sub Questions */}
                                 {q.subQuestions && q.subQuestions.length > 0 && (
                                     <div className="pl-4 space-y-4 mt-2 border-l border-gray-100 dark:border-white/10">
                                         {q.subQuestions.map((sq, sqIdx) => (
                                             <div key={sq.id} className="flex gap-3">
                                                 <span className="font-bold text-[11px] text-gray-600 dark:text-gray-400 w-4">({String.fromCharCode(97 + sqIdx)})</span>
                                                 <div className="flex-1">
                                                     <div className="flex justify-between items-start gap-4">
                                                                                                                 <MarkdownText className="text-sm text-gray-800 dark:text-gray-200 leading-snug">
                                                                                                                     {sq.text}
                                                                                                                 </MarkdownText>
                                                         <span className="text-[10px] font-bold text-gray-400 shrink-0">({sq.marks})</span>
                                                     </div>
                                                     
                                                     {sq.type === 'Multiple Choice' && sq.options && (
                                                         <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mt-2">
                                                             {sq.options.map((opt: string, optI: number) => (
                                                                 <div key={optI} 
                                                                     className="flex items-center gap-2 cursor-pointer group/sub-option"
                                                                     onClick={() => toggleAnswer(sq.id, optI)}
                                                                 >
                                                                     <div className={`w-4 h-4 rounded-full border flex items-center justify-center text-[8px] font-bold shrink-0 transition-all ${
                                                                         mode === 'student'
                                                                            ? (studentAnswers[sq.id] === optI 
                                                                                ? 'bg-blue-600 border-blue-600 text-white shadow-sm scale-110'
                                                                                : 'border-gray-200 dark:border-white/20 group-hover/sub-option:border-blue-500 group-hover/sub-option:text-blue-600')
                                                                            : (sq.correctAnswerIndex === optI ? 'bg-green-600 border-green-600 text-white' : 'border-gray-200 opacity-50')
                                                                     }`}>
                                                                         {mode === 'moderator' && sq.correctAnswerIndex === optI ? <CheckCircle className="w-2.5 h-2.5" /> : String.fromCharCode(65 + optI)}
                                                                     </div>
                                                                                                                                         <div className={`text-[11px] ${mode === 'moderator' && sq.correctAnswerIndex === optI ? 'text-green-700 font-bold' : 'text-gray-700 dark:text-gray-300'}`}>
                                                                                                                                             <MarkdownText>{opt}</MarkdownText>
                                                                                                                                         </div>
                                                                 </div>
                                                             ))}
                                                         </div>
                                                     )}
                                                     
                                                      {(sq.type === 'Short Answer' || sq.type === 'Essay') && (
                                                         <div className="mt-2 text-xs">
                                                            {mode === 'student' ? (
                                                                <Textarea placeholder="Type answer..." className="bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10 min-h-[80px] text-xs resize-y focus-visible:ring-blue-500/20" />
                                                            ) : (
                                                                 <div className="space-y-1">
                                                                     <span className="text-[8px] font-bold text-blue-700 uppercase tracking-widest block">Answer</span>
                                                                     <div dangerouslySetInnerHTML={{ __html: renderMathInHtml(formatMarkSchemeAnswer(sq.modelAnswer || sq.correctAnswer)) }} className="overflow-x-auto" />
                                                                </div>
                                                            )}
                                                         </div>
                                                     )}
                                                 </div>
                                             </div>
                                         ))}
                                     </div>
                                 )}
                             </div>
                            <div className="font-bold text-gray-900 dark:text-gray-100 text-right w-10 sm:w-14 text-[10px] sm:text-xs pt-1 shrink-0">
                                ({q.marks})
                            </div>
                         </div>
                    </div>
                 );
            })}
        </CardContent>

        <CardFooter className="py-6 border-t border-gray-100 dark:border-white/5 bg-gray-50/10 dark:bg-white/5 flex flex-col items-center gap-1">
            <p className="text-[9px] text-muted-foreground font-bold uppercase tracking-widest">End of Assessment Paper</p>
            <p className="text-[8px] text-muted-foreground opacity-50">Generated by Sonoma Assessment System</p>
        </CardFooter>
      </Card>
      )}
    </div>
  );
};

export default AssessmentPreview;
